import { useEffect, useState } from "react";
import type { Figura, Karta, Kolor } from "@gierki/shared";
import { FIGURY_ZADANIA, KOLORY } from "@gierki/shared";
import { useCountdown, toSeconds, useOnChange, vibrate } from "../hooks.ts";
import { Karta as Panel, Komunikat, Odznaka, Przycisk } from "../ui.tsx";
import { KartaDoGry, Rewers, SymbolKoloru } from "./KartaDoGry.tsx";

/**
 * Makao — ekran partii.
 *
 * Przyklejona jest **ręka na dole**: bez niej nie da się zaplanować ruchu, więc
 * musi być widoczna razem z wierzchem stosu. Ta sama decyzja co stojak
 * w Scrabble.
 *
 * Ekran nie zna **żadnej** reguły. To, co wolno zagrać, przychodzi z serwera
 * jako `legalne: number[]` — reguł jest tu tyle (kara, postój, żądanie koloru,
 * żądanie figury, dama na wszystko, ostatnia karta funkcyjna), że drugi ich
 * komplet w kliencie rozjechałby się przy pierwszej zmianie ustawień.
 */

export interface MakaoView {
  faza: "lobby" | "tura" | "zadanie-koloru" | "zadanie-figury" | "koniec";
  players: string[];
  hostId: string;
  canStart: boolean;

  aktywnyId: string | null;
  mojaTura: boolean;

  mojaReka: Karta[];
  liczbaKart: Record<string, number>;
  taliaCount: number;
  stosCount: number;
  wierzch: Karta | null;

  kara: { ile: number; rodzaj: "dwojki" | "krol"; kierunek: 1 | -1 } | null;
  karaNa: string | null;
  postoje: Record<string, number>;
  postojWisi: boolean;
  zadanieKoloru: Kolor | null;
  zadanieFigury: { figura: Figura; autor: string; do: string | null } | null;
  wymaganeZadanie: "kolor" | "figura" | null;

  legalne: number[];
  mogeDobierac: boolean;
  mogePasowac: boolean;

  makaoOkno: { gracz: string; do: number } | null;

  wyszli: string[];
  punkty: Record<string, number> | null;
  kartNaStart: number;
  karaZaBrakMakao: number;
  deadlineAt: number | null;
}

/**
 * Ile miejsca zajmuje karta w ręce.
 *
 * Do ośmiu kart mieszczą się obok siebie w pełnych 56 px na 390-pikselowym
 * ekranie. Powyżej ręka przechodzi w **wachlarz**: karty nachodzą na siebie,
 * widoczny jest lewy pasek z figurą i kolorem, a wybrana unosi się i odsłania
 * w całości.
 *
 * Uczciwa uwaga do projektu: przy trzynastu kartach **nie da się** dać każdej
 * pełnych 56 px pola dotyku — 13 × 56 = 728 px, a ekran ma 390. Karta wcześniej
 * leży na późniejszej (malejący `z-index`), więc pełne 56 px ma zawsze ta
 * wybrana i pierwsza z brzegu; pozostałe mają tyle, ile widać. Rekompensujemy
 * to **w pionie**: pasek ręki jest wyższy od karty, więc cel dotykowy ma
 * pełne 56 px wysokości niezależnie od liczby kart.
 */
const SZEROKOSC_KARTY = 56;
const MIN_PASEK = 32;

function odstep(ile: number, szerokoscEkranu: number): number {
  if (ile <= 1) return SZEROKOSC_KARTY;
  const dostepne = Math.max(szerokoscEkranu - 32, 200);
  const naKarte = (dostepne - SZEROKOSC_KARTY) / (ile - 1);
  return Math.max(MIN_PASEK, Math.min(SZEROKOSC_KARTY + 4, naKarte));
}

const NAZWY_KOLOROW: Record<Kolor, string> = {
  pik: "Pik",
  kier: "Kier",
  karo: "Karo",
  trefl: "Trefl",
};

export function Makao({
  view,
  nicki,
  mojeId,
  onStart,
  onZagraj,
  onZadajKolor,
  onZadajFigure,
  onDobierz,
  onPas,
  onMakao,
  onLapy,
}: {
  view: MakaoView;
  nicki: Record<string, string>;
  mojeId: string;
  onStart: () => void;
  onZagraj: (karta: Karta) => void;
  onZadajKolor: (kolor: Kolor) => void;
  onZadajFigure: (figura: Figura | null) => void;
  onDobierz: () => void;
  onPas: () => void;
  onMakao: () => void;
  onLapy: (na: string) => void;
}) {
  const [wybrana, setWybrana] = useState<number | null>(null);
  const [szerokosc, setSzerokosc] = useState(390);

  useEffect(() => {
    const zmierz = () => setSzerokosc(window.innerWidth);
    zmierz();
    window.addEventListener("resize", zmierz);
    return () => window.removeEventListener("resize", zmierz);
  }, []);

  // Zmiana ręki albo wierzchu unieważnia wybór: to, co wolno zagrać, właśnie
  // się zmieniło, a wyłożenie „tej, co była zaznaczona" byłoby niespodzianką.
  const znacznik = `${view.mojaReka.join(",")}|${view.wierzch ?? ""}|${view.faza}`;
  useEffect(() => setWybrana(null), [znacznik]);

  useOnChange(view.mojaTura, (teraz) => {
    if (teraz) vibrate([40, 60, 40]);
  });

  if (view.faza === "lobby") return <Lobby view={view} nicki={nicki} onStart={onStart} />;

  const nick = (id: string | null): string => (id ? (nicki[id] ?? id) : "?");
  const legalna = (i: number): boolean => view.legalne.includes(i);

  const dotknij = (i: number) => {
    if (!legalna(i)) return;
    if (wybrana === i) onZagraj(view.mojaReka[i] as Karta);
    else setWybrana(i);
  };

  return (
    <>
      {view.makaoOkno ? (
        <PasekMakao view={view} nick={nick} mojeId={mojeId} onMakao={onMakao} onLapy={onLapy} />
      ) : null}

      <Stol view={view} nicki={nicki} mojeId={mojeId} />
      <Srodek view={view} nick={nick} />

      {view.faza === "koniec" ? <Wyniki view={view} nicki={nicki} /> : null}

      {view.faza === "koniec" ? null : (
        <div className="sticky bottom-0 -mx-4 border-t border-obrys bg-tlo px-4 pb-2 pt-3 space-y-3">
          {view.wymaganeZadanie && view.aktywnyId === mojeId ? (
            /*
             * Panel żądania zasłania rękę **celowo**: w tej fazie ręka i tak
             * nie jest do niczego potrzebna, a wybór ma być jednym dotknięciem.
             */
            <PanelZadania
              czego={view.wymaganeZadanie}
              onKolor={onZadajKolor}
              onFigura={onZadajFigure}
            />
          ) : (
            <>
              <Reka
                view={view}
                wybrana={wybrana}
                szerokosc={szerokosc}
                onDotknij={dotknij}
                legalna={legalna}
              />
              <Sterowanie
                view={view}
                nick={nick}
                wybrana={wybrana}
                onZagraj={() => wybrana !== null && onZagraj(view.mojaReka[wybrana] as Karta)}
                onDobierz={onDobierz}
                onPas={onPas}
              />
            </>
          )}
        </div>
      )}
    </>
  );
}

function Lobby({
  view,
  nicki,
  onStart,
}: {
  view: MakaoView;
  nicki: Record<string, string>;
  onStart: () => void;
}) {
  return (
    <>
      <Panel>
        <div className="text-sm text-tekst-slaby mb-2">Gracze</div>
        <div className="space-y-1">
          {view.players.map((id) => (
            <div key={id} className="flex justify-between">
              <span>{nicki[id] ?? id}</span>
              {id === view.hostId ? <Odznaka>host</Odznaka> : null}
            </div>
          ))}
        </div>
      </Panel>
      <Komunikat>
        Każdy dostaje {view.kartNaStart} kart. Dokładasz jedną zgodną kolorem albo figurą; nie masz
        czym — dobierasz. Dwójka i trójka dokładają karne dobieranie, czwórka zabiera kolejkę, król
        pik i kier biją na pięć, walet żąda figury, as zmienia kolor. Zostajesz z jedną kartą —
        naciśnij „Makao”, zanim ktoś krzyknie „Łapy!”.
      </Komunikat>
      {view.canStart ? (
        <Przycisk wariant="glowny" pelna onClick={onStart}>
          Rozdajemy
        </Przycisk>
      ) : (
        <Komunikat>Czekamy, aż host rozda karty.</Komunikat>
      )}
    </>
  );
}

/**
 * Pasek okna „makao": odliczanie i jeden przycisk.
 *
 * Odliczanie jest lokalne, skorygowane o zmierzoną różnicę zegarów — czas jest
 * serwerowy. Pasek **nie zatrzymuje gry**: obok niego można normalnie zagrać.
 */
function PasekMakao({
  view,
  nick,
  mojeId,
  onMakao,
  onLapy,
}: {
  view: MakaoView;
  nick: (id: string | null) => string;
  mojeId: string;
  onMakao: () => void;
  onLapy: (na: string) => void;
}) {
  const okno = view.makaoOkno;
  const zostalo = toSeconds(useCountdown(okno?.do ?? null));
  if (!okno) return null;
  const moje = okno.gracz === mojeId;

  return (
    <div className="sticky top-0 z-10 -mx-4 flex items-center gap-3 border-b border-obrys bg-tlo px-4 py-2">
      <span className="flex-1 truncate text-sm">
        {moje ? "Została Ci jedna karta!" : `${nick(okno.gracz)} ma jedną kartę`}
      </span>
      <span className="tabular text-tekst-slaby">{zostalo ?? 0}s</span>
      {moje ? (
        <Przycisk wariant="glowny" onClick={onMakao}>
          Makao!
        </Przycisk>
      ) : (
        <Przycisk wariant="gorny" onClick={() => onLapy(okno.gracz)}>
          Łapy!
        </Przycisk>
      )}
    </div>
  );
}

/** Rząd graczy: ile kto ma kart, kto na ruchu, kto stoi, kto już wyszedł. */
function Stol({
  view,
  nicki,
  mojeId,
}: {
  view: MakaoView;
  nicki: Record<string, string>;
  mojeId: string;
}) {
  return (
    <div className="flex flex-wrap justify-center gap-2">
      {view.players.map((id) => {
        const wyszedl = view.wyszli.includes(id);
        const postoj = view.postoje[id] ?? 0;
        const zada = view.zadanieFigury?.autor === id ? view.zadanieFigury : null;

        return (
          <div
            key={id}
            className={[
              "flex flex-col items-center gap-1 rounded-2xl border px-3 py-2 min-w-20",
              view.aktywnyId === id ? "border-akcent" : "border-obrys",
              wyszedl ? "opacity-40" : "",
            ].join(" ")}
          >
            <div className="flex items-end gap-1">
              <Rewers szerokosc={18} />
              <span className="text-lg tabular font-semibold">{view.liczbaKart[id] ?? 0}</span>
            </div>
            <div className="text-xs truncate max-w-24">{id === mojeId ? "Ty" : (nicki[id] ?? id)}</div>
            <div className="flex flex-wrap justify-center gap-1">
              {wyszedl ? <Odznaka zielona>wyszedł</Odznaka> : null}
              {postoj > 0 ? <Odznaka>postój ×{postoj}</Odznaka> : null}
              {zada ? <Odznaka aktywna>żąda: {zada.figura}</Odznaka> : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Środek stołu: talia, wierzch stosu i — gdy wisi — licznik kar. */
function Srodek({ view, nick }: { view: MakaoView; nick: (id: string | null) => string }) {
  return (
    <div className="space-y-2">
      {view.kara ? (
        /*
         * Licznik kar to najważniejsza liczba na ekranie i musi być czytelna
         * z fotela pasażera. Strzałka niesie kierunek: król kier bije w tył.
         */
        <div className="flex items-center justify-center gap-2 text-blad">
          <span className="text-3xl font-semibold tabular">+{view.kara.ile}</span>
          <span className="text-2xl">{view.kara.kierunek === -1 ? "↑" : "↓"}</span>
          <span className="text-sm">na {nick(view.karaNa)}</span>
        </div>
      ) : null}

      <div className="flex items-end justify-center gap-6">
        <div className="flex flex-col items-center gap-1">
          <Rewers szerokosc={64} />
          <span className="text-xs tabular text-tekst-slaby">{view.taliaCount} w talii</span>
        </div>

        <div className="flex flex-col items-center gap-1">
          <div
            className="rounded-xl p-0.5"
            style={{
              // Obwódka w kolorze żądanym przez asa — żądanie widać stale.
              boxShadow: view.zadanieKoloru ? "0 0 0 3px var(--color-akcent)" : undefined,
            }}
          >
            {view.wierzch ? <KartaDoGry karta={view.wierzch} szerokosc={80} /> : <Rewers szerokosc={80} />}
          </div>
          <span className="text-xs tabular text-tekst-slaby">{view.stosCount} na stosie</span>
        </div>
      </div>

      {view.zadanieKoloru ? (
        <div className="flex items-center justify-center gap-2 text-sm">
          <span className="text-tekst-slaby">Żądany kolor:</span>
          <SymbolKoloru kolor={view.zadanieKoloru} className="w-4 h-4" />
          <span>{NAZWY_KOLOROW[view.zadanieKoloru]}</span>
        </div>
      ) : null}
    </div>
  );
}

/** Ręka: wachlarz z nachodzącymi kartami, nielegalne przygaszone i nieklikalne. */
function Reka({
  view,
  wybrana,
  szerokosc,
  onDotknij,
  legalna,
}: {
  view: MakaoView;
  wybrana: number | null;
  szerokosc: number;
  onDotknij: (i: number) => void;
  legalna: (i: number) => boolean;
}) {
  const krok = odstep(view.mojaReka.length, szerokosc);
  const wysokosc = Math.round(SZEROKOSC_KARTY * 1.5) + 16;

  if (view.mojaReka.length === 0) {
    return <div className="text-center text-tekst-slaby">Nie masz już kart — oglądasz.</div>;
  }

  return (
    <div className="flex justify-center overflow-x-auto" style={{ minHeight: `${wysokosc}px` }}>
      <div className="relative" style={{ width: `${krok * (view.mojaReka.length - 1) + SZEROKOSC_KARTY}px`, height: `${wysokosc}px` }}>
        {view.mojaReka.map((karta, i) => (
          <span
            key={`${karta}-${i}`}
            className="absolute bottom-0"
            style={{
              left: `${i * krok}px`,
              // Karta wcześniejsza leży **na** późniejszej, a wybrana na
              // wszystkich: dzięki temu ta, którą właśnie wybrano, ma pełne
              // pole dotyku i widać ją w całości.
              zIndex: wybrana === i ? view.mojaReka.length + 1 : view.mojaReka.length - i,
            }}
          >
            <KartaDoGry
              karta={karta}
              szerokosc={SZEROKOSC_KARTY}
              wybrana={wybrana === i}
              przygaszona={!legalna(i)}
              onClick={() => onDotknij(i)}
            />
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * Panel żądania: cztery kolory albo sześć figur i „bez żądania".
 *
 * Lista figur jest **stała** (`5`…`10`) i przychodzi ze `shared` — ten sam
 * zbiór, którym waliduje serwer. Żądanie waletów zapętla grę, a asów zamienia
 * ją w loterię kolorów.
 */
function PanelZadania({
  czego,
  onKolor,
  onFigura,
}: {
  czego: "kolor" | "figura";
  onKolor: (kolor: Kolor) => void;
  onFigura: (figura: Figura | null) => void;
}) {
  if (czego === "kolor") {
    return (
      <div className="space-y-2">
        <div className="text-sm text-tekst-slaby">Jaki kolor?</div>
        <div className="grid grid-cols-4 gap-2">
          {KOLORY.map((kolor) => (
            <button
              key={kolor}
              onClick={() => onKolor(kolor)}
              className="flex min-h-16 flex-col items-center justify-center gap-1 rounded-2xl border border-obrys active:scale-95 transition-transform"
            >
              <SymbolKoloru kolor={kolor} className="w-7 h-7" />
              <span className="text-xs">{NAZWY_KOLOROW[kolor]}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="text-sm text-tekst-slaby">Jakiej figury żądasz?</div>
      <div className="grid grid-cols-4 gap-2">
        {FIGURY_ZADANIA.map((figura) => (
          <Przycisk key={figura} onClick={() => onFigura(figura)}>
            {figura}
          </Przycisk>
        ))}
        <Przycisk wariant="cichy" className="col-span-2" onClick={() => onFigura(null)}>
          Bez żądania
        </Przycisk>
      </div>
    </div>
  );
}

function Sterowanie({
  view,
  nick,
  wybrana,
  onZagraj,
  onDobierz,
  onPas,
}: {
  view: MakaoView;
  nick: (id: string | null) => string;
  wybrana: number | null;
  onZagraj: () => void;
  onDobierz: () => void;
  onPas: () => void;
}) {
  if (!view.mojaTura) {
    return (
      <div className="text-center text-tekst-slaby">
        {view.wymaganeZadanie ? `${nick(view.aktywnyId)} wybiera żądanie` : `Ruch: ${nick(view.aktywnyId)}`}
      </div>
    );
  }

  const podKara = view.kara !== null;

  return (
    <div className="space-y-2">
      {view.postojWisi ? (
        <Komunikat>Masz postój — zagraj czwórkę albo odczekaj kolejkę.</Komunikat>
      ) : view.legalne.length === 0 && !podKara ? (
        <Komunikat>Nie masz czym zagrać. Dobierz kartę.</Komunikat>
      ) : null}

      <div className="grid grid-cols-2 gap-2">
        <Przycisk
          wariant="glowny"
          disabled={wybrana === null}
          onClick={onZagraj}
          className="col-span-2"
        >
          {wybrana === null ? "Wybierz kartę" : "Zagraj"}
        </Przycisk>
        <Przycisk disabled={!view.mogeDobierac} onClick={onDobierz}>
          {podKara ? `Przyjmij +${view.kara?.ile}` : "Dobierz"}
        </Przycisk>
        <Przycisk disabled={!view.mogePasowac} onClick={onPas}>
          {view.postojWisi ? "Odczekaj" : "Pas"}
        </Przycisk>
      </div>
    </div>
  );
}

/**
 * Wyniki: kolejność wyjścia plus punkty karne z ręki.
 *
 * Mnożnik jest tak dobrany, że kolejność wyjścia zawsze bije majątek ręki —
 * karne rozstrzygają wyłącznie remisy między tymi, którzy nie wyszli wcale.
 */
function Wyniki({ view, nicki }: { view: MakaoView; nicki: Record<string, string> }) {
  const punkty = view.punkty ?? {};
  const kolejnosc = [...view.players].sort((a, b) => (punkty[b] ?? 0) - (punkty[a] ?? 0));

  return (
    <Panel>
      <div className="text-sm text-tekst-slaby mb-3">Wynik końcowy</div>
      <div className="space-y-2">
        {kolejnosc.map((id, miejsce) => (
          <div key={id} className="flex items-center gap-3">
            <span className="w-6 text-tekst-slaby tabular">{miejsce + 1}.</span>
            <span className="flex-1 truncate">
              {nicki[id] ?? id}
              {miejsce === 0 ? " 🏆" : ""}
            </span>
            <span className="tabular text-tekst-slaby">{punkty[id] ?? 0}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}
